import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Save, Mail, User, Shield, Users } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Input from '../../common/Input';
import { updateUser } from '../../../services/userService';
import { getUserTeams, assignManagerTeams } from '../../../services/membershipService';
import { useTeam } from '../../../context/TeamContext';
import './UserManagement.css';

/**
 * EditUserModal Component
 * 
 * Modal for editing existing users (admin only).
 * Includes team membership management.
 */
export default function EditUserModal({ isOpen, onClose, user, onSuccess }) {
  const { authorisedTeams } = useTeam();
  const realTeams = authorisedTeams.filter(t => t.id !== null);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'viewer',
    teamIds: []
  });
  const [originalTeamIds, setOriginalTeamIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Initialize form data when user changes
  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        email: user.email || '',
        name: user.name || '',
        role: user.role || 'viewer',
        teamIds: []
      });
      // Fetch user's current team memberships
      setLoadingTeams(true);
      getUserTeams(user.id)
        .then(teams => {
          const ids = (teams || []).map(t => t.team_id || t.id).filter(Boolean);
          setFormData(prev => ({ ...prev, teamIds: ids }));
          setOriginalTeamIds(ids);
        })
        .catch(() => { /* non-fatal — teams will show empty */ })
        .finally(() => setLoadingTeams(false));
    }
  }, [user, isOpen]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    else if (formData.name.trim().length < 2) newErrors.name = 'Name must be at least 2 characters';
    if (!formData.role) newErrors.role = 'Role is required';
    if (formData.teamIds.length === 0) newErrors.teamIds = 'Select at least one team';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      // Update user profile (name, role)
      await updateUser(user.id, {
        name: formData.name,
        role: formData.role
      });

      // Update team memberships if changed
      const teamsChanged = JSON.stringify([...formData.teamIds].sort()) !== JSON.stringify([...originalTeamIds].sort());
      if (teamsChanged && formData.teamIds.length > 0) {
        await assignManagerTeams(user.id, formData.teamIds);
      }

      if (onSuccess) onSuccess('User updated successfully');
      onClose();
    } catch (error) {
      setErrors({
        submit: error.response?.data?.message || error.message || 'Failed to update user'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setErrors({});
      onClose();
    }
  };

  if (!user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit User"
      size="md"
      closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={loading} icon={<Save size={18} />}>Save Changes</Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="user-form">
        {/* Email Field (Read-only) */}
        <div className="form-group">
          <label htmlFor="edit-email" className="form-label"><Mail size={16} /> Email Address</label>
          <Input id="edit-email" name="email" type="email" value={formData.email} disabled={true} readOnly={true} />
          <p className="field-hint">Email cannot be changed</p>
        </div>

        {/* Name Field */}
        <div className="form-group">
          <label htmlFor="edit-name" className="form-label"><User size={16} /> Full Name</label>
          <Input id="edit-name" name="name" type="text" value={formData.name} onChange={handleChange} placeholder="John Smith" error={errors.name} disabled={loading} autoComplete="name" />
          {errors.name && <span className="error-text">{errors.name}</span>}
        </div>

        {/* Role Field */}
        <div className="form-group">
          <label htmlFor="edit-role" className="form-label"><Shield size={16} /> Role</label>
          <select id="edit-role" name="role" value={formData.role} onChange={handleChange} className={`form-select ${errors.role ? 'error' : ''}`} disabled={loading}>
            <option value="viewer">Viewer</option>
            <option value="manager">Report Manager</option>
            <option value="admin">Admin</option>
          </select>
          {errors.role && <span className="error-text">{errors.role}</span>}
          <p className="field-hint">Viewer: Read-only access | Report Manager: Can manage reports | Admin: Full access</p>
        </div>

        {/* Team Membership */}
        <div className="form-group">
          <label className="form-label"><Users size={16} /> Team Membership</label>
          {loadingTeams ? (
            <p className="field-hint">Loading team memberships...</p>
          ) : (
            <div className="folder-checkbox-list" style={{ maxHeight: '160px', overflowY: 'auto', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'var(--glass-bg)' }}>
              {realTeams.length === 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>No teams available</span>}
              {realTeams.map(team => (
                <label key={team.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)', cursor: 'pointer', padding: '0.25rem 0' }}>
                  <input
                    type="checkbox"
                    checked={formData.teamIds.includes(team.id)}
                    onChange={() => {
                      setFormData(prev => ({
                        ...prev,
                        teamIds: prev.teamIds.includes(team.id)
                          ? prev.teamIds.filter(id => id !== team.id)
                          : [...prev.teamIds, team.id]
                      }));
                      if (errors.teamIds) setErrors(prev => ({ ...prev, teamIds: '' }));
                    }}
                    disabled={loading}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          )}
          {errors.teamIds && <span className="error-text">{errors.teamIds}</span>}
        </div>

        {/* Submit Error */}
        {errors.submit && <div className="form-error">{errors.submit}</div>}
      </form>
    </Modal>
  );
}

EditUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired
  }),
  onSuccess: PropTypes.func
};
